import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './dialog';
import { Button } from './button';
import { ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { clientAPI } from '../../utils/crmApi';
import { CLIENT_STATUS_CONFIG } from '../../utils/clientValidation';
import { getButtonClasses, getStatusClasses } from '../../utils/brandingUtils';
import { useBranding } from '../../contexts/BrandingContext';
import { toast } from 'react-hot-toast';

const ClientStatusValidationModal = ({ 
    isOpen, 
    onClose, 
    client, 
    onStatusUpdated,
    onProceedToTaskAssignment 
}) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const { branding } = useBranding();

    if (!client) return null;

    const statusConfig = CLIENT_STATUS_CONFIG[client.status?.toLowerCase()] || CLIENT_STATUS_CONFIG.inactive;
    const canAssignTasks = statusConfig.canAssignTasks;

    const handleStatusUpdate = async (newStatus) => {
        try {
            setIsUpdating(true);
            await clientAPI.update(client._id, { status: newStatus });
            
            // Update the client object
            const updatedClient = { ...client, status: newStatus };
            onStatusUpdated?.(updatedClient);
            
            toast.success('Client status updated successfully');
            onClose();
            
            // Proceed to task assignment after status update
            setTimeout(() => {
                onProceedToTaskAssignment?.(updatedClient);
            }, 500);
            
        } catch (error) {
            console.error('Error updating client status:', error);
            toast.error('Failed to update client status');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleProceedAnyway = () => {
        onClose();
        // Pass a flag to indicate user bypassed validation
        onProceedToTaskAssignment?.(client, true);
    };

    const getStatusIcon = () => {
        switch (client.status?.toLowerCase()) {
            case 'prospect':
                return <ExclamationTriangleIcon className="h-6 w-6 text-blue-500" />;
            case 'paused':
                return <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />;
            case 'churned':
            case 'closed_lost':
                return <XCircleIcon className="h-6 w-6 text-red-500" />;
            case 'inactive':
            case 'archived':
                return <XCircleIcon className="h-6 w-6 text-muted-foreground" />;
            default:
                return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-500" />;
        }
    };

    const getStatusColor = () => {
        return statusConfig.color;
    };

    const getActionButtons = () => {
        if (canAssignTasks) {
            return (
                <Button 
                    onClick={() => onProceedToTaskAssignment?.(client)}
                    className={getButtonClasses('primary', branding)}
                >
                    Proceed to Task Assignment
                </Button>
            );
        }

        // For inactive clients, show options to activate or proceed anyway
        const canBeActivated = ['prospect', 'paused', 'inactive'].includes(client.status?.toLowerCase());
        
        return (
            <div className="flex gap-3">
                {canBeActivated && (
                    <Button 
                        onClick={() => handleStatusUpdate('active')}
                        disabled={isUpdating}
                        className={getButtonClasses('success', branding)}
                    >
                        {isUpdating ? 'Updating...' : 'Activate Client'}
                    </Button>
                )}
                <Button 
                    onClick={handleProceedAnyway}
                    variant="outline"
                    className={getButtonClasses('outline', branding)}
                >
                    Proceed Anyway
                </Button>
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        {getStatusIcon()}
                        Client Status Validation
                    </DialogTitle>
                    <DialogDescription>
                        Task assignment validation for {client.name}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Current Status Display */}
                    <div className="bg-muted rounded-lg p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-foreground">Current Status</p>
                                <p className="text-sm text-muted-foreground">{statusConfig.label}</p>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                                getStatusColor() === 'green' ? 'bg-green-100 text-green-800' :
                                getStatusColor() === 'blue' ? 'bg-blue-100 text-blue-800' :
                                getStatusColor() === 'orange' ? 'bg-orange-100 text-orange-800' :
                                getStatusColor() === 'red' ? 'bg-red-100 text-red-800' :
                                'bg-muted text-foreground'
                            }`}>
                                {statusConfig.label}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            {statusConfig.description}
                        </p>
                    </div>

                    {/* Validation Message */}
                    <div className={`rounded-lg p-4 ${
                        canAssignTasks 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                        <div className="flex items-start gap-3">
                            {canAssignTasks ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
                            ) : (
                                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
                            )}
                            <div>
                                <p className={`text-sm font-medium ${
                                    canAssignTasks ? 'text-green-800' : 'text-yellow-800'
                                }`}>
                                    {canAssignTasks 
                                        ? 'Client is ready for task assignment'
                                        : 'Task assignment requires active status'
                                    }
                                </p>
                                <p className={`text-xs mt-1 ${
                                    canAssignTasks ? 'text-green-700' : 'text-yellow-700'
                                }`}>
                                    {canAssignTasks 
                                        ? 'You can proceed to assign tasks to this client.'
                                        : 'Consider activating the client first, or proceed anyway if needed.'
                                    }
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button 
                        onClick={onClose}
                        variant="outline"
                        className={getButtonClasses('outline', branding)}
                    >
                        Cancel
                    </Button>
                    {getActionButtons()}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ClientStatusValidationModal;
